/* eslint-disable no-nested-ternary */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import {
    CircularProgress, FormControl, Menu, MenuItem
} from '@mui/material';
import DOMPurify from 'dompurify';
import moment from 'moment';
import React, { ReactNode } from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { ConnectedProps, connect } from 'react-redux';
import { getSplitBreaksLabelId } from '../../libs/lessonSplit';
import { LESSON_FILTERS } from '../../resources/constants';
import '../../resources/css/lessonDetails.css';
import { ApplicationState } from '../../store/types';
import Button from '../common/button';
import Popper from '../common/popper';
import SelectField from '../common/selectField';

/**
 * Parent component that represent a lesson details for the booking page
 */
type ReduxProps = ConnectedProps<typeof connector>;

interface BookingLessonDetailsIProps {
    lesson: {
        price: number;
        id: number;
        type: string;

        // Period card
        startDate: string;
        endDate: string;
        hasEndDateImpact?: boolean;
        infoDays: string;
        infoBeginDays: string;
        infoEndDays: string;
        infoNumberDaysMin: string;
        infoNumberDaysMax: string;
        exclusionDates?: string[];

        // Days (private lessons only)
        daysLabel?: string;
        daysValues?: number[];
        daysOptions?: (typeof MenuItem)[];
        selectedDayIndex?: number;
        onDayChange?: (event: any) => void;

        // Hours card
        startTimeLabel?: string;
        startTimeValues?: string[];
        startTimeOptions?: (typeof MenuItem)[];
        selectedStartTimeIndex?: number;
        onStartTimeChange?: (event: any) => void;

        endTimeLabel?: string;
        endTimeValues?: string[];
        endTimeOptions?: (typeof MenuItem)[];
        selectedEndTimeIndex?: number;
        onEndTimeChange?: (event: any) => void;

        splitBreaks?: string[];

        // Summary card
        numberOfDays?: number;
        lessonDuration?: string | ReactNode;
        totalHours: any;

        // Other
        level: {
            name: string;
            memo: string;
        };
        participants: string;
        meetingPoints: {
            name: string;
            memo: string;
        }[];
        meetingPointOptions: any;
        selectedMeetingPointIndex: number;
        onMeetingPointChange: (event: any) => void;
    };
    isCheckAvailabilityLoading: boolean;
    handleClickButtonSelect(event: any): void;
    schoolsList: any;
    schoolInstructorsConfiguration: any;
}
interface BookingLessonDetailsState {
    tooltipInfo: any;
    anchorEl: any;
    tooltipDaysText: any;
    timeSelectAnchorEl: HTMLElement | null;
}

type BookingLessonDetailsProps = BookingLessonDetailsIProps & ReduxProps & WrappedComponentProps;

class BookingLessonDetails extends React.Component<BookingLessonDetailsProps, BookingLessonDetailsState> {
    constructor(props: BookingLessonDetailsProps) {
        super(props);
        this.state = {
            tooltipInfo: undefined,
            anchorEl: undefined,
            tooltipDaysText: undefined,
            timeSelectAnchorEl: null,
        };
    }

    componentDidMount() {
        const { intl, lesson } = this.props;
        // Get all texts
        const weekdays = intl.formatMessage({ id: 'products.weekdays' }).split('_');
        const dayAllowed = intl.formatMessage({ id: 'products.dayAllowed' });
        const startDayAllowed = intl.formatMessage({ id: 'products.startDayAllowed' });
        const endDayAllowed = intl.formatMessage({ id: 'products.endDayAllowed' });
        const nbDays = intl.formatMessage({ id: 'products.nbDays' });
        const {
            infoDays, infoBeginDays, infoEndDays, infoNumberDaysMin, infoNumberDaysMax,
            exclusionDates,
        } = lesson;

        // Get every numeric day [0123456]  => [Monday...Sunday]
        const infoDaysTable = infoDays && infoDays.split('');
        let infoDaysTMP = '';
        // Convert numeric to weekday
        if (Array.isArray(infoDaysTable)) {
            infoDaysTable.forEach((e) => {
                infoDaysTMP += `${weekdays[parseInt(e, 10)]}, `;
            });
        } else {
            infoDaysTMP += weekdays[parseInt(infoDaysTable, 10)];
        }

        // Get every numeric day [0123456] => [Monday...Sunday]
        const infoBeginDaysTable = infoBeginDays && infoBeginDays.split('');
        let infoBeginDaysTMP = '';
        // Convert numeric to weekday
        if (Array.isArray(infoBeginDaysTable)) {
            infoBeginDaysTable.forEach((e) => {
                infoBeginDaysTMP += `${weekdays[parseInt(e, 10)]}, `;
            });
        } else {
            infoBeginDaysTMP += weekdays[parseInt(infoBeginDaysTable, 10)];
        }

        // Get every numeric day [0123456] => [Monday...Sunday]
        const infoEndDaysTable = infoEndDays && infoEndDays.split('');
        let infoEndDaysTMP = '';
        // Convert numeric to weekday
        if (Array.isArray(infoEndDaysTable)) {
            infoEndDaysTable.forEach((e) => {
                infoEndDaysTMP += `${weekdays[parseInt(e, 10)]}, `;
            });
        } else {
            infoEndDaysTMP += weekdays[parseInt(infoEndDaysTable, 10)];
        }

        // Format exclusion dates for display (DD.MM.YYYY)
        let exclusionDatesText = '';
        if (exclusionDates && exclusionDates.length > 0) {
            const formattedDates = exclusionDates
                .map((d) => moment(d).format('DD.MM.YYYY'))
                .join(', ');
            exclusionDatesText = `<p><b>${intl.formatMessage({ id: 'products.excludedDates' })}:</b> ${formattedDates}</p>`;
        }

        const tooltipDaysText = `<p><b>${dayAllowed}:</b> ${infoDaysTMP}</p>
                                <p><b>${startDayAllowed}:</b> ${infoBeginDaysTMP}</p>
                                <p><b>${endDayAllowed}:</b> ${infoEndDaysTMP}</p>
                                <p><b>${nbDays}:</b> ${infoNumberDaysMin} - ${infoNumberDaysMax}</p>
                                ${exclusionDatesText}`;
        this.setState({ tooltipDaysText });
    }

    handleClickInfo = (event: any, item: any) => this.setState(
        {
            tooltipInfo: item,
            anchorEl: event.currentTarget,
        },
    );

    handleOpenTimeSelect = (event: React.MouseEvent<HTMLElement>) => {
        this.setState({ timeSelectAnchorEl: event.currentTarget });
    };

    handleCloseTimeSelect = () => this.setState({ timeSelectAnchorEl: null });

    render() {
        const {
            intl,
            lesson,
            isCheckAvailabilityLoading,
            handleClickButtonSelect,
            schoolsList,
            schoolInstructorsConfiguration,
        } = this.props;
        const {
            tooltipDaysText, tooltipInfo, anchorEl, timeSelectAnchorEl,
        } = this.state;
        return (
            <>
                <div>
                    {/* Grille unifiée avec toutes les informations */}
                    <div className="bl-item-details">
                        <div className="bl-item-details-top-cards">
                            {/* Card Période */}
                            {
                            // ESS Sion, special lesson, workaround for the date display
                                [2810, 3003].includes(lesson.id)
                                    ? null
                                    : (
                                        <div className="bl-item-details-card period-card">
                                            {/* Date de début - Point de départ */}
                                            <div className="bl-item-details-card-field">
                                                <div className="bl-item-details-card-label">
                                                    {intl.formatMessage({ id: 'booking.startDate' })}
                                                </div>
                                                <div className="bl-item-details-card-value">
                                                    (
                                                    {moment(lesson.startDate, 'DD.MM.YYYY').format('dd')}
                                                    )
                                                    {' '}
                                                    {lesson.startDate}
                                                </div>
                                            </div>

                                            {/* Date de fin - Calculée automatiquement */}
                                            <div className="bl-item-details-card-field">
                                                <div className="bl-item-details-card-label">
                                                    {intl.formatMessage({ id: 'booking.endDate' })}
                                                </div>
                                                <div className="bl-item-details-card-value">
                                                    <span
                                                        style={
                                                            lesson.hasEndDateImpact
                                                                ? { fontWeight: 600 }
                                                                : {}
                                                        }
                                                    >
                                                        (
                                                        {moment(lesson.endDate, 'DD.MM.YYYY').format('dd')}
                                                        )
                                                        {' '}
                                                        {lesson.endDate}
                                                    </span>
                                                    {lesson.hasEndDateImpact && (
                                                        <WarningIcon
                                                            className="bl-item-details-icon"
                                                            style={{
                                                                height: '20px',
                                                                width: '20px',
                                                                flexShrink: 0,
                                                                cursor: 'pointer',
                                                                color: '#ff9800',
                                                                background: 'none',
                                                                borderRadius: 0,
                                                                boxShadow: 'none',
                                                            }}
                                                            onClick={(event) => this.handleClickInfo(
                                                                event,
                                                                `<p><strong>${intl.formatMessage({ id: 'booking.endDateWarning' })}</strong></p><br/>${tooltipDaysText}`,
                                                            )}
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            {/* Jours (stepper) - Pour cours privés uniquement */}
                                            {lesson.daysLabel && lesson.daysValues && lesson.daysOptions && (
                                                <div className="bl-item-details-card-field">
                                                    <div className="bl-item-details-card-label">{lesson.daysLabel}</div>
                                                    {
                                                        lesson.daysOptions.length > 1
                                                            ? (
                                                                <div className="bl-item-details-stepper">
                                                                    <button
                                                                        type="button"
                                                                        className="bl-item-details-stepper-button"
                                                                        onClick={() => lesson.onDayChange?.({ target: { value: Math.max(0, lesson.selectedDayIndex! - 1) } })}
                                                                        disabled={lesson.selectedDayIndex === 0}
                                                                    >
                                                                        −
                                                                    </button>
                                                                    <div className="bl-item-details-stepper-value">
                                                                        {lesson.daysValues[lesson.selectedDayIndex!]}
                                                                        {' '}
                                                                        {intl.formatMessage({ id: lesson.daysValues[lesson.selectedDayIndex!] > 1 ? 'booking.days' : 'booking.day' })}
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="bl-item-details-stepper-button"
                                                                        onClick={() => lesson.onDayChange?.({ target: { value: Math.min(lesson.daysOptions!.length - 1, lesson.selectedDayIndex! + 1) } })}
                                                                        disabled={lesson.selectedDayIndex === lesson.daysOptions.length - 1}
                                                                    >
                                                                        +
                                                                    </button>
                                                                </div>
                                                            )
                                                            : (
                                                                <div className="bl-item-details-card-value">
                                                                    {lesson.daysValues[0]}
                                                                    {' '}
                                                                    {intl.formatMessage({ id: 'booking.days' })}
                                                                </div>
                                                            )
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    )
                            }

                            {/* Card Horaires */}
                            <div className="bl-item-details-card hours-card">
                                {/* For private lessons: display times as text with a select icon */}
                                {lesson.type === LESSON_FILTERS.PRIVATE_LESSON && lesson.startTimeLabel && lesson.startTimeValues && lesson.endTimeValues ? (
                                    <div className="bl-item-details-card-field bl-item-details-time-range">
                                        <div className="bl-item-details-time-display">
                                            <div className="bl-item-details-time-column">
                                                <div className="bl-item-details-card-label">{lesson.startTimeLabel}</div>
                                                <div
                                                    className={`bl-item-details-card-value${lesson.startTimeOptions && lesson.startTimeOptions.length > 1 ? ' bl-item-details-card-value-clickable' : ''}`}
                                                    onClick={lesson.startTimeOptions && lesson.startTimeOptions.length > 1 ? this.handleOpenTimeSelect : undefined}
                                                    role={lesson.startTimeOptions && lesson.startTimeOptions.length > 1 ? 'button' : undefined}
                                                    tabIndex={lesson.startTimeOptions && lesson.startTimeOptions.length > 1 ? 0 : undefined}
                                                    onKeyDown={lesson.startTimeOptions && lesson.startTimeOptions.length > 1 ? (e: any) => { if (e.key === 'Enter' || e.key === ' ') this.handleOpenTimeSelect(e); } : undefined}
                                                >
                                                    {moment(lesson.startTimeValues[lesson.selectedStartTimeIndex!]).format(`HH[${intl.formatMessage({ id: 'booking.h' })}]mm`)}
                                                    {lesson.startTimeOptions && lesson.startTimeOptions.length > 1 && (
                                                        timeSelectAnchorEl
                                                            ? <ArrowDropUpIcon className="bl-item-details-select-arrow" />
                                                            : <ArrowDropDownIcon className="bl-item-details-select-arrow" />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="bl-item-details-time-column">
                                                <div className="bl-item-details-card-label">{lesson.endTimeLabel}</div>
                                                <div
                                                    className={`bl-item-details-card-value${lesson.startTimeOptions && lesson.startTimeOptions.length > 1 ? ' bl-item-details-card-value-clickable' : ''}`}
                                                    onClick={lesson.startTimeOptions && lesson.startTimeOptions.length > 1 ? this.handleOpenTimeSelect : undefined}
                                                    role={lesson.startTimeOptions && lesson.startTimeOptions.length > 1 ? 'button' : undefined}
                                                    tabIndex={lesson.startTimeOptions && lesson.startTimeOptions.length > 1 ? 0 : undefined}
                                                    onKeyDown={lesson.startTimeOptions && lesson.startTimeOptions.length > 1 ? (e: any) => { if (e.key === 'Enter' || e.key === ' ') this.handleOpenTimeSelect(e); } : undefined}
                                                >
                                                    {moment(lesson.endTimeValues[lesson.selectedStartTimeIndex!]).format(`HH[${intl.formatMessage({ id: 'booking.h' })}]mm`)}
                                                    {lesson.startTimeOptions && lesson.startTimeOptions.length > 1 && (
                                                        timeSelectAnchorEl
                                                            ? <ArrowDropUpIcon className="bl-item-details-select-arrow" />
                                                            : <ArrowDropDownIcon className="bl-item-details-select-arrow" />
                                                    )}
                                                </div>
                                            </div>
                                            {lesson.startTimeOptions && lesson.startTimeOptions.length > 1 && (
                                                <Menu
                                                    anchorEl={timeSelectAnchorEl}
                                                    open={Boolean(timeSelectAnchorEl)}
                                                    onClose={this.handleCloseTimeSelect}
                                                    anchorOrigin={{
                                                        vertical: 'bottom',
                                                        horizontal: 'left',
                                                    }}
                                                    transformOrigin={{
                                                        vertical: 'top',
                                                        horizontal: 'left',
                                                    }}
                                                >
                                                    {lesson.startTimeValues.map((startTime, index) => (
                                                        <MenuItem
                                                            key={startTime}
                                                            selected={index === lesson.selectedStartTimeIndex}
                                                            onClick={() => {
                                                                lesson.onStartTimeChange?.({ target: { value: index } } as any);
                                                                this.handleCloseTimeSelect();
                                                            }}
                                                        >
                                                            {moment(startTime).format(`HH[${intl.formatMessage({ id: 'booking.h' })}]mm`)}
                                                            {' - '}
                                                            {moment(lesson.endTimeValues![index]).format(`HH[${intl.formatMessage({ id: 'booking.h' })}]mm`)}
                                                        </MenuItem>
                                                    ))}
                                                </Menu>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* For group lessons: separate start and end time selects */}
                                        {lesson.startTimeLabel && lesson.startTimeOptions && (
                                            <div className="bl-item-details-card-field">
                                                <div className="bl-item-details-card-label">{lesson.startTimeLabel}</div>
                                                {
                                                    lesson.startTimeOptions.length > 1
                                                        ? (
                                                            <FormControl fullWidth>
                                                                <SelectField
                                                                    parentClasses="bl-item-details-select-span"
                                                                    value={lesson.selectedStartTimeIndex}
                                                                    defaultValue={0}
                                                                    onChange={lesson.onStartTimeChange}
                                                                    name="select_start_time"
                                                                    border
                                                                    height={40}
                                                                >
                                                                    {lesson.startTimeOptions}
                                                                </SelectField>
                                                            </FormControl>
                                                        )
                                                        : (
                                                            <div className="bl-item-details-card-value">
                                                                {moment(lesson.startTimeValues![0]).format(`HH[${intl.formatMessage({ id: 'booking.h' })}]mm`)}
                                                            </div>
                                                        )
                                                }
                                            </div>
                                        )}

                                        {lesson.endTimeLabel && lesson.endTimeValues && (
                                            <div className="bl-item-details-card-field">
                                                <div className="bl-item-details-card-label">{lesson.endTimeLabel}</div>
                                                {
                                                    lesson.endTimeOptions && lesson.endTimeOptions.length > 1
                                                        ? (
                                                            <FormControl fullWidth>
                                                                <SelectField
                                                                    defaultValue={0}
                                                                    parentClasses="bl-item-details-select-span"
                                                                    value={lesson.selectedEndTimeIndex}
                                                                    onChange={lesson.onEndTimeChange}
                                                                    name="select_end_time"
                                                                    border
                                                                    height={40}
                                                                >
                                                                    {lesson.endTimeOptions}
                                                                </SelectField>
                                                            </FormControl>
                                                        )
                                                        : (
                                                            <div className="bl-item-details-card-value">
                                                                {moment(lesson.endTimeValues[lesson.selectedEndTimeIndex || 0]).format(`HH[${intl.formatMessage({ id: 'booking.h' })}]mm`)}
                                                            </div>
                                                        )
                                                }
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Duration split (pause de midi) - Lié aux heures */}
                                {lesson.splitBreaks && lesson.splitBreaks.length > 0 && (
                                    <div className="bl-item-details-card-field">
                                        <div className="bl-item-details-card-label">
                                            {intl.formatMessage({ id: getSplitBreaksLabelId(lesson.splitBreaks) })}
                                        </div>
                                        <div className="bl-item-details-card-value">{lesson.splitBreaks.join(', ')}</div>
                                    </div>
                                )}
                            </div>
                            {/* Point de rendez-vous - Prend l'espace restant */}
                            <div className="bl-item-details-card meeting-card">
                                <div className="bl-item-details-card-field">
                                    <div className="bl-item-details-card-label">{intl.formatMessage({ id: 'booking.meeting' })}</div>
                                    {
                                        lesson.meetingPointOptions.length > 1
                                            ? (
                                                <div className="bl-item-details-card-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <FormControl fullWidth>
                                                        <SelectField
                                                            parentClasses="bl-item-details-select-span"
                                                            value={lesson.selectedMeetingPointIndex}
                                                            onChange={lesson.onMeetingPointChange}
                                                            name="select_meeting"
                                                            defaultValue={0}
                                                            border
                                                            height={40}
                                                        >
                                                            {lesson.meetingPointOptions}
                                                        </SelectField>
                                                    </FormControl>
                                                    {
                                                        lesson.selectedMeetingPointIndex === -1
                                                    && (
                                                        <WarningIcon
                                                            style={{ color: 'var(--error-color)', flexShrink: 0 }}
                                                            fontSize="small"
                                                        />
                                                    )
                                                    }
                                                    {
                                                        lesson.meetingPoints[lesson.selectedMeetingPointIndex] && lesson.meetingPoints[lesson.selectedMeetingPointIndex].memo && lesson.meetingPoints[lesson.selectedMeetingPointIndex].memo.length > 0
                                                            ? (
                                                                <InfoIcon
                                                                    className="bl-item-details-icon"
                                                                    style={{ height: '18px', width: '18px', flexShrink: 0 }}
                                                                    onClick={(event) => this.handleClickInfo(event, lesson.meetingPoints[lesson.selectedMeetingPointIndex].memo)}
                                                                />
                                                            )
                                                            : null
                                                    }
                                                </div>
                                            )
                                            : lesson.meetingPointOptions.length === 1
                                                ? (
                                                    <div className="bl-item-details-card-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {lesson.meetingPoints.length > 0 ? lesson.meetingPoints[0].name : ''}
                                                        {
                                                            lesson.meetingPoints[0] && lesson.meetingPoints[0].memo && lesson.meetingPoints[0].memo.length > 0
                                                                ? (
                                                                    <InfoIcon
                                                                        className="bl-item-details-icon"
                                                                        style={{ height: '18px', width: '18px', flexShrink: 0 }}
                                                                        onClick={(event) => this.handleClickInfo(event, lesson.meetingPoints[0].memo)}
                                                                    />
                                                                )
                                                                : null
                                                        }
                                                    </div>
                                                )
                                                : <div className="bl-item-details-card-value" style={{ color: 'var(--error-color)' }}>{intl.formatMessage({ id: 'booking.meetingError' })}</div>
                                    }
                                </div>
                            </div>
                        </div>

                        {/* Card Résumé - Informations complètes avec prix et bouton */}
                        <div className="bl-item-details-card summary-card">
                            <div className="bl-item-details-summary-container">
                                <div className="bl-item-details-summary-title">
                                    {intl.formatMessage({ id: 'booking.summary' })}
                                    {' '}
                                    :
                                </div>
                                <div className="bl-item-details-summary-wrapper">
                                    <div className="bl-item-details-summary-content">
                                        {/* Nombre de jours */}
                                        {lesson.numberOfDays && (
                                            <div className="bl-item-details-card-field">
                                                <div className="bl-item-details-card-label">
                                                    {intl.formatMessage({ id: lesson.numberOfDays > 1 ? 'booking.days' : 'booking.day' })}
                                                </div>
                                                <div className="bl-item-details-card-value">
                                                    {lesson.numberOfDays}
                                                </div>
                                            </div>
                                        )}

                                        {/* Durée d'un cours */}
                                        {lesson.lessonDuration && (
                                            <div className="bl-item-details-card-field">
                                                <div className="bl-item-details-card-label">
                                                    {intl.formatMessage({ id: 'booking.lessonDuration' })}
                                                </div>
                                                <div className="bl-item-details-card-value">
                                                    {lesson.lessonDuration}
                                                </div>
                                            </div>
                                        )}

                                        {/* Heures totales */}
                                        <div className="bl-item-details-card-field">
                                            <div className="bl-item-details-card-label">
                                                {intl.formatMessage({ id: 'booking.totalHours' })}
                                            </div>
                                            <div className="bl-item-details-card-value">{lesson.totalHours}</div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                            <div className="bl-item-details-summary-next">
                                <span className="bl-item-details-summary-price">
                                    {
                                        lesson.price === -1
                                            ? (
                                                <CircularProgress size={20} style={{ color: 'var(--primary-color)' }} />
                                            )
                                            : lesson.price === -2 || lesson.meetingPointOptions.length === 0
                                                ? (
                                                    <span style={{ color: 'var(--error-color)' }}>
                                                        {intl.formatMessage({ id: 'booking.noPrice' })}
                                                    </span>
                                                )
                                                : (`CHF ${lesson.price}.-`)
                                    }
                                </span>
                                <Button
                                    containerClasses="bl-item-details-summary-button"
                                    onClick={handleClickButtonSelect}
                                    disabled={lesson.meetingPointOptions.length === 0 || lesson.selectedMeetingPointIndex === -1 || lesson.price <= -1 || (schoolsList.length === 0 && lesson.type === LESSON_FILTERS.PRIVATE_LESSON) || (lesson.type === LESSON_FILTERS.PRIVATE_LESSON && !schoolInstructorsConfiguration)}
                                    loading={isCheckAvailabilityLoading}
                                >
                                    {intl.formatMessage({ id: isCheckAvailabilityLoading ? 'order.checking' : 'booking.select' })}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                <Popper
                    open={Boolean(tooltipInfo)}
                    onClose={() => this.setState({ tooltipInfo: undefined })}
                    anchorEl={anchorEl}
                    position="top"
                >
                    <div className="bl-item-details-icon-popup">
                        {tooltipInfo && (
                            <span
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(tooltipInfo, {
                                        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'span', 'a'],
                                        ALLOWED_ATTR: ['href', 'target', 'rel']
                                    })
                                }}
                            />
                        )}
                    </div>
                </Popper>
            </>
        );
    }
}

const mapStateToProps = (store: ApplicationState) => ({
    windowWidth: store.windowSize.width,
    schoolsList: store.booking.schoolsList,
    schoolInstructorsConfiguration: store.booking.schoolInstructorsConfiguration,
});
const connector = connect(mapStateToProps);

export default injectIntl(connector(BookingLessonDetails));
